import { BookOpen, Type, ImageIcon, CalendarPlus, FileText, Inbox, LifeBuoy } from "lucide-react";

const STEPS = [
  {
    icon: Type,
    title: "Edit any wording — the “Text” tab",
    body: "Every heading, paragraph, label and button on the site lives here. Open a section (e.g. “Home” or “Footer”), click into a box, type your changes, then press “Save all changes”. Your words appear on the live site straight away.",
  },
  {
    icon: FileText,
    title: "Edit a whole page — the “Pages” tab",
    body: "For the main body of each page, use Pages. Edit the heading and the rich text, click “Save draft” to keep your work, then “Publish” when you’re ready for visitors to see it.",
  },
  {
    icon: ImageIcon,
    title: "Swap any photo — the “Images” tab",
    body: "Every picture on the site is here. Find the image you’d like to change, click “Upload new image”, and choose a photo from your device. Landscape photos suit the wide banners; upright (portrait) photos suit the tall picture frames.",
  },
  {
    icon: CalendarPlus,
    title: "Offer session times — the “Availability” tab",
    body: "Add the dates and times you’re free (e.g. “12 August – Morning”). Visitors can only request the times you add here, and a time disappears the moment someone books it — so you’ll never be double-booked.",
  },
  {
    icon: Inbox,
    title: "See who’s been in touch — “Bookings” & “Enquiries”",
    body: "New booking requests and messages arrive here (and by email). Confirm or decline bookings with a click. Declining a booking frees that time slot again for someone else.",
  },
];

export default function HelpPanel({ firstVisit = false }) {
  return (
    <div className="max-w-3xl">
      {firstVisit && (
        <div className="rounded-2xl border border-brand-forest/30 bg-brand-accent/50 px-5 py-4 mb-6" data-testid="onboarding-welcome">
          <p className="font-body text-sm text-brand-ink leading-relaxed">
            👋 <strong>Welcome to your new website dashboard!</strong> We’ve opened this quick guide for you.
            You can come back to it anytime from the <strong>Help</strong> tab.
          </p>
        </div>
      )}
      <div className="flex items-center gap-3 mb-2">
        <span className="h-11 w-11 rounded-full bg-brand-lavender-soft flex items-center justify-center">
          <BookOpen className="text-brand-lavender-dark" size={20} strokeWidth={1.5} />
        </span>
        <h2 className="font-heading text-3xl text-brand-ink">How to edit your website</h2>
      </div>
      <p className="font-body text-brand-slate mb-8">
        Welcome, Isabelle. You can change almost anything on your site yourself, right here — no technical
        knowledge needed. Here’s a gentle guide to each part. Nothing you do can break the site, so please
        explore freely.
      </p>

      <div className="space-y-4">
        {STEPS.map((s) => (
          <div key={s.title} className="rounded-2xl border border-brand-line bg-white p-6 flex gap-5">
            <span className="h-11 w-11 shrink-0 rounded-full bg-brand-accent flex items-center justify-center">
              <s.icon className="text-brand-forest" size={20} strokeWidth={1.5} />
            </span>
            <div>
              <h3 className="font-heading text-xl text-brand-ink mb-1.5">{s.title}</h3>
              <p className="font-body text-sm text-brand-slate leading-relaxed">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-brand-line bg-brand-lavender-soft/50 p-6 flex gap-4">
        <LifeBuoy className="text-brand-lavender-dark shrink-0" size={22} strokeWidth={1.5} />
        <div>
          <h3 className="font-heading text-xl text-brand-ink mb-1.5">A few gentle reminders</h3>
          <ul className="font-body text-sm text-brand-slate leading-relaxed list-disc pl-5 space-y-1">
            <li>Text changes go live as soon as you press Save. Page changes go live when you press Publish.</li>
            <li>If you clear a text box by mistake, the site quietly keeps the original wording — nothing goes blank.</li>
            <li>Always keep a copy of important wording somewhere safe before large edits, just in case.</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
